import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Runner } from 'react-runner';
import { useRecoilValue } from 'recoil';
import { v4 as uuidv4 } from 'uuid';

import {
  ChainlitContext,
  IAction,
  ICustomElement,
  IElement,
  sessionIdState,
  useAuth,
  useChatInteract
} from '@chainlit/react-client';

import Alert from '@/components/Alert';

import Imports from './Imports';
import * as Renderer from './Renderer';

// Stable across renders — react-runner remounts when `scope` identity changes,
// which wipes local state in Quiz / Flashcards (index jumps back to 0).
const runnerImports = { ...Imports, '@/components/renderer': Renderer };

function propsEqual(
  a: Record<string, unknown> | undefined,
  b: Record<string, unknown> | undefined
): boolean {
  if (a === b) return true;
  try {
    return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  } catch {
    return false;
  }
}

const CustomElement = memo(
  function CustomElement({ element }: { element: ICustomElement }) {
    const apiClient = useContext(ChainlitContext);
    const sessionId = useRecoilValue(sessionIdState);
    const { sendMessage } = useChatInteract();
    const { user } = useAuth();

    const [sourceCode, setSourceCode] = useState<string>();
    const [error, setError] = useState<string>();

    const elementRef = useRef(element);
    elementRef.current = element;
    const sessionIdRef = useRef(sessionId);
    sessionIdRef.current = sessionId;

    useEffect(() => {
      let cancelled = false;
      apiClient
        .get(`/public/elements/${element.name}.jsx`)
        .then(async (res) => {
          const text = await res.text();
          if (!cancelled) setSourceCode(text);
        })
        .catch((err) => {
          if (!cancelled) setError(String(err));
        });
      return () => {
        cancelled = true;
      };
    }, [element.name, apiClient]);

    const updateElement = useCallback(
      (nextProps: Record<string, unknown>) => {
        const sid = sessionIdRef.current;
        if (!sid) return;
        const nextElement: IElement = {
          ...elementRef.current,
          props: nextProps
        };
        return apiClient.updateElement(nextElement, sid);
      },
      [apiClient]
    );

    const deleteElement = useCallback(() => {
      const sid = sessionIdRef.current;
      if (!sid) return;
      return apiClient.deleteElement(elementRef.current, sid);
    }, [apiClient]);

    const callAction = useCallback(
      (action: IAction) => {
        const sid = sessionIdRef.current;
        if (!sid) return;
        return apiClient.callAction(action, sid);
      },
      [apiClient]
    );

    const sendUserMessage = useCallback(
      (message: string) => {
        return sendMessage({
          threadId: '',
          id: uuidv4(),
          name: user?.identifier || 'User',
          type: 'user_message',
          output: message,
          createdAt: new Date().toISOString(),
          metadata: { location: window.location.href }
        });
      },
      [sendMessage, user]
    );

    // Deep-stable props: share polling replaces element objects every 15s with
    // identical questions — stringify keeps Runner from remounting.
    const propsKey = useMemo(() => {
      try {
        return JSON.stringify(element.props ?? null);
      } catch {
        return String(Date.now());
      }
    }, [element.props]);

    const props = useMemo(() => {
      return JSON.parse(propsKey) as Record<string, unknown>;
    }, [propsKey]);

    const onRendered = useCallback((err?: Error) => {
      setError((prev) => {
        const next = err?.message;
        return prev === next ? prev : next;
      });
    }, []);

    const scope = useMemo(
      () => ({
        import: runnerImports,
        props,
        apiClient,
        updateElement,
        deleteElement,
        callAction,
        sendUserMessage
      }),
      [
        props,
        apiClient,
        updateElement,
        deleteElement,
        callAction,
        sendUserMessage
      ]
    );

    if (error) return <Alert variant="error">{error}</Alert>;
    if (!sourceCode) return null;

    return (
      <div className={`${element.display}-custom flex flex-col flex-grow`}>
        <Runner code={sourceCode} scope={scope} onRendered={onRendered} />
      </div>
    );
  },
  (prev, next) =>
    prev.element.id === next.element.id &&
    prev.element.name === next.element.name &&
    prev.element.display === next.element.display &&
    propsEqual(prev.element.props, next.element.props)
);

export default CustomElement;
